/**
 * Vehicle Table Component
 * Responsive table/card view for vehicles with actions
 */

import { useState } from 'react';
import { MoreVertical, AlertTriangle, Wrench } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Badge } from '../../ui/badge';
import { Card } from '../../ui/card';
import { ComplianceBadge } from '../ComplianceBadge';
import { AssignDriverModal } from './AssignDriverModal';
import { MaintenanceModal } from './MaintenanceModal';
import type { VehicleDTO } from '../../../services/vehicle.service';

interface VehicleTableProps {
  vehicles: VehicleDTO[];
}

export function VehicleTable({ vehicles }: VehicleTableProps) {
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isMaintenanceModalOpen, setIsMaintenanceModalOpen] = useState(false);

  const handleAssignDriver = (vehicleId: string) => {
    setSelectedVehicleId(vehicleId);
    setIsAssignModalOpen(true);
  };

  const handleMaintenance = (vehicleId: string) => {
    setSelectedVehicleId(vehicleId);
    setIsMaintenanceModalOpen(true);
  };

  return (
    <>
      {/* Desktop View */}
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Vehicle #</TableHead>
              <TableHead>Make/Model</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Driver</TableHead>
              <TableHead>Odometer</TableHead>
              <TableHead>DOT Compliance</TableHead>
              <TableHead>Maintenance</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {vehicles.map((vehicle) => (
              <TableRow key={vehicle.id}>
                <TableCell className="font-medium">{vehicle.vehicleNumber}</TableCell>
                <TableCell>
                  {vehicle.make} {vehicle.model}
                  <div className="text-xs text-gray-500">{vehicle.year}</div>
                </TableCell>
                <TableCell className="capitalize">
                  {vehicle.type.replace('_', ' ')}
                </TableCell>
                <TableCell>
                  <Badge
                    variant={
                      vehicle.status === 'available'
                        ? 'success'
                        : vehicle.status === 'assigned'
                        ? 'default'
                        : vehicle.status === 'maintenance'
                        ? 'warning'
                        : 'destructive'
                    }
                  >
                    {vehicle.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  {vehicle.driverAssignment ? (
                    <div className="text-sm">
                      {vehicle.driverAssignment.driverName}
                    </div>
                  ) : (
                    <button
                      onClick={() => handleAssignDriver(vehicle.id)}
                      className="text-sm text-primary-600 hover:text-primary-700"
                    >
                      Assign Driver
                    </button>
                  )}
                </TableCell>
                <TableCell>{vehicle.odometer.toLocaleString()} mi</TableCell>
                <TableCell>
                  <ComplianceBadge
                    isCompliant={vehicle.dotCompliance.isCompliant}
                    isExpiringSoon={vehicle.dotCompliance.isExpiringSoon}
                  />
                  <div className="mt-1 text-xs text-gray-500">
                    Exp: {new Date(vehicle.dotCompliance.expirationDate).toLocaleDateString()}
                  </div>
                </TableCell>
                <TableCell>
                  {vehicle.maintenanceDue ? (
                    <button
                      onClick={() => handleMaintenance(vehicle.id)}
                      className="flex items-center gap-1 text-sm text-yellow-600 hover:text-yellow-700"
                    >
                      <AlertTriangle className="h-4 w-4" />
                      Due
                    </button>
                  ) : (
                    <button
                      onClick={() => handleMaintenance(vehicle.id)}
                      className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-700"
                    >
                      <Wrench className="h-4 w-4" />
                      Record
                    </button>
                  )}
                </TableCell>
                <TableCell>
                  <button className="rounded-md p-1 hover:bg-gray-100">
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile View */}
      <div className="grid gap-4 lg:hidden">
        {vehicles.map((vehicle) => (
          <Card key={vehicle.id} className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold">{vehicle.vehicleNumber}</div>
                <div className="text-sm text-gray-600">
                  {vehicle.make} {vehicle.model} {vehicle.year}
                </div>
              </div>
              <Badge
                variant={
                  vehicle.status === 'available'
                    ? 'success'
                    : vehicle.status === 'assigned'
                    ? 'default'
                    : vehicle.status === 'maintenance'
                    ? 'warning'
                    : 'destructive'
                }
              >
                {vehicle.status}
              </Badge>
            </div>

            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Type:</span>
                <span className="capitalize">{vehicle.type.replace('_', ' ')}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">Driver:</span>
                {vehicle.driverAssignment ? (
                  <span>{vehicle.driverAssignment.driverName}</span>
                ) : (
                  <button
                    onClick={() => handleAssignDriver(vehicle.id)}
                    className="text-primary-600 hover:text-primary-700"
                  >
                    Assign
                  </button>
                )}
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">Odometer:</span>
                <span>{vehicle.odometer.toLocaleString()} mi</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-gray-600">DOT:</span>
                <div className="text-right">
                  <ComplianceBadge
                    isCompliant={vehicle.dotCompliance.isCompliant}
                    isExpiringSoon={vehicle.dotCompliance.isExpiringSoon}
                  />
                  <div className="mt-1 text-xs text-gray-500">
                    Exp: {new Date(vehicle.dotCompliance.expirationDate).toLocaleDateString()}
                  </div>
                </div>
              </div>

              <div className="flex justify-between pt-2">
                {vehicle.maintenanceDue ? (
                  <button
                    onClick={() => handleMaintenance(vehicle.id)}
                    className="flex items-center gap-1 rounded-md bg-yellow-50 px-3 py-2 text-yellow-700 hover:bg-yellow-100"
                  >
                    <AlertTriangle className="h-4 w-4" />
                    Maintenance Due
                  </button>
                ) : (
                  <button
                    onClick={() => handleMaintenance(vehicle.id)}
                    className="flex items-center gap-1 rounded-md bg-gray-50 px-3 py-2 text-gray-700 hover:bg-gray-100"
                  >
                    <Wrench className="h-4 w-4" />
                    Record Maintenance
                  </button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Modals */}
      {selectedVehicleId && (
        <>
          <AssignDriverModal
            vehicleId={selectedVehicleId}
            open={isAssignModalOpen}
            onClose={() => {
              setIsAssignModalOpen(false);
              setSelectedVehicleId(null);
            }}
          />
          <MaintenanceModal
            vehicleId={selectedVehicleId}
            open={isMaintenanceModalOpen}
            onClose={() => {
              setIsMaintenanceModalOpen(false);
              setSelectedVehicleId(null);
            }}
          />
        </>
      )}
    </>
  );
}
